import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { SubmitButton } from '@/components/ui/submit-button'
import type { ChatActionWithValue } from '../../models/chat'

const CODE_LENGTH = 6
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface ChatVerifyEmailProps {
  email?: string
  handleSubmit: (data: ChatActionWithValue) => void
  readonly?: boolean
}

/**
 * Proves the address on an account we created and signed in ourselves. Not
 * `ChatOtp` — that one is the DOT-number entry and carries its own test-number
 * buttons. Changing the address matters here: this step blocks the application,
 * so a typo in the funnel would otherwise be a dead end.
 */
export function ChatVerifyEmail({ email, handleSubmit, readonly }: ChatVerifyEmailProps) {
  const [code, setCode] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [newEmail, setNewEmail] = useState(email ?? '')

  // Every submit carries `content` — the chat only echoes actions that do — but never the code itself.
  function submitCode(value: string) {
    handleSubmit({ content: { component: 'P', content: '••••••' }, value: { code: value } })
  }

  function submitEmail(event: React.FormEvent) {
    event.preventDefault()
    const value = newEmail.trim()
    if (!EMAIL_REGEX.test(value)) return
    setIsEditing(false)
    setCode('')
    handleSubmit({
      content: { component: 'P', content: `Use ${value} instead` },
      value: { action: 'CHANGE_EMAIL', email: value },
    })
  }

  // ChatAction wraps actions in items-end, so a bare form shrinks to the input's intrinsic width.
  if (isEditing) {
    return (
      <form onSubmit={submitEmail} className="mt-2 flex w-full max-w-[300px] flex-col gap-3">
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          autoFocus
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          disabled={readonly}
          aria-label="Email address"
          // The code boxes' height, so the buttons below don't jump when switching between the two.
          className="h-11"
        />
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setIsEditing(false)} disabled={readonly}>
            Cancel
          </Button>
          <SubmitButton disabled={readonly || !EMAIL_REGEX.test(newEmail.trim())} />
        </div>
      </form>
    )
  }

  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex justify-end">
        <InputOTP
          maxLength={CODE_LENGTH}
          value={code}
          onChange={(value) => {
            setCode(value)
            if (value.length === CODE_LENGTH && !readonly) submitCode(value)
          }}
          autoComplete="one-time-code"
          disabled={readonly}
        >
          <InputOTPGroup>
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={readonly}
          onClick={() => {
            setCode('')
            handleSubmit({
              content: { component: 'P', content: 'Send a new code' },
              value: { action: 'RESEND' },
            })
          }}
        >
          Send a new code
        </Button>
        <Button type="button" variant="ghost" disabled={readonly} onClick={() => setIsEditing(true)}>
          Use a different email
        </Button>
        <SubmitButton onClick={() => submitCode(code)} disabled={readonly || code.length < CODE_LENGTH} />
      </div>
    </div>
  )
}
